// ─── Grouped navigation config ────────────────────────────────────────────
// Extracted from Layout.jsx for separation of concerns.
// Each item has a `perm` key matching backend/permissions.py PAGE_PERMISSIONS.
// Sidebar filters items where the current user lacks the perm.
import {
  LayoutDashboard, ShoppingCart, Package, Boxes, Factory,
  IndianRupee, Users, FileCheck, BarChart3,
  Receipt, ClipboardList, BookOpen,
  FlaskConical, Gift, ShieldCheck, CalendarDays, Truck,
  TrendingUp, TrendingDown, Zap, FileText, Brain, AlertTriangle,
  Sparkles, Inbox, History, Gauge, FileBarChart, PartyPopper,
  CreditCard, ClipboardCheck, Hammer, Layers, Bell, Cloud, Landmark, Banknote, MessageCircle,
} from 'lucide-react';

export const NAV_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    // Per-group color identity for the sidebar — operators recall a
    // module by colour as much as by name. Each group carries:
    //   accent       — Tailwind text class for the active-item label
    //   bg           — soft tint that surfaces when item is active or hovered
    //   ring         — left-rail accent applied via border-l on active item
    //   sectionLabel — uppercase section heading colour (works in both themes)
    color: { accent: 'text-amber-400',   bg: 'bg-amber-500/10',  ring: 'border-amber-400',  sectionLabel: 'text-amber-500 dark:text-amber-400' },
    items: [
      { path: '/dashboard',    label: 'Dashboard',    icon: LayoutDashboard, perm: 'dashboard' },
      { path: '/inbox',        label: 'Universal Inbox', icon: Inbox,         perm: 'notifications' },
      { path: '/notifications',label: 'Alerts',       icon: Bell,             perm: 'notifications' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales & Orders',
    icon: ShoppingCart,
    color: { accent: 'text-emerald-400', bg: 'bg-emerald-500/10', ring: 'border-emerald-400', sectionLabel: 'text-emerald-600 dark:text-emerald-400' },
    items: [
      { path: '/sales',              label: 'Sales Pipeline',   icon: ShoppingCart,  perm: 'sales_pipeline' },
      { path: '/sales-legacy',       label: 'Sales (Kanban)',   icon: LayoutDashboard, perm: 'sales' },
      { path: '/bill-log',           label: 'Bill Log',         icon: FileText,       perm: 'vouchers' },
      { path: '/enterprise',         label: 'Enterprise',       icon: Receipt,        perm: 'vouchers' },
      { path: '/sales-calendar',     label: 'Sales Calendar',   icon: CalendarDays,   perm: 'sales_calendar' },
      { path: '/dispatch-calendar',  label: 'Dispatch Cal.',    icon: Truck,          perm: 'sales_calendar' },
      { path: '/tracking-dispatches', label: 'Tracking Dispatches', icon: Truck,      perm: 'sales_calendar' },
      { path: '/admin/portal-orders',   label: 'Portal Orders',   icon: ShoppingCart, perm: 'admin_portal_orders' },
      { path: '/admin/portal-accounts', label: 'Portal Accounts', icon: Users,        perm: 'admin_portal_accounts', adminOnly: true },
    ],
  },
  {
    id: 'purchases',
    label: 'Purchases',
    icon: Package,
    color: { accent: 'text-rose-400', bg: 'bg-rose-500/10', ring: 'border-rose-400', sectionLabel: 'text-rose-600 dark:text-rose-400' },
    items: [
      { path: '/purchases',          label: 'Purchases',        icon: Package, perm: 'purchases' },
      { path: '/purchase-calendar',  label: 'Purchase Cal.',    icon: Truck,   perm: 'purchase_calendar' },
      { path: '/procurement-ai',     label: 'Procurement AI',   icon: Zap,     perm: 'procurement_intelligence' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory & Lots',
    icon: Boxes,
    color: { accent: 'text-sky-400', bg: 'bg-sky-500/10', ring: 'border-sky-400', sectionLabel: 'text-sky-600 dark:text-sky-400' },
    items: [
      { path: '/inward-lot',   label: 'Inward + Lot', icon: Boxes, external: true, perm: 'inventory' },
      { path: '/inventory',    label: 'Inventory',    icon: Boxes,                   perm: 'inventory' },
      { path: '/lot-history',  label: 'Lot History',  icon: History,                 perm: 'inventory' },
      { path: '/reorder',      label: 'Reorder Points', icon: Gauge,                 perm: 'inventory' },
    ],
  },
  {
    id: 'production',
    label: 'Production',
    icon: Factory,
    color: { accent: 'text-violet-400', bg: 'bg-violet-500/10', ring: 'border-violet-400', sectionLabel: 'text-violet-600 dark:text-violet-400' },
    items: [
      { path: '/factory-command-center', label: 'Factory Command Center', icon: Factory, perm: 'production' },
      { path: '/production',       label: 'Production',       icon: Factory,        perm: 'production' },
      { path: '/qc',               label: 'Quality Control',  icon: ClipboardCheck, perm: 'quality_check' },
      { path: '/production-tasks', label: 'Production Tasks', icon: Hammer,         perm: 'production' },
      { path: '/transformations',  label: 'Transformations',  icon: Layers,         perm: 'transformations' },
      { path: '/daily-summary',    label: 'Daily Summary',    icon: ClipboardList,  perm: 'production' },
      { path: '/rnd-chains',       label: 'Recipe Chains',    icon: FlaskConical,   perm: 'rnd' },
      { path: '/rnd',              label: 'R&D',              icon: FlaskConical,   perm: 'rnd' },
      { path: '/samples',          label: 'Samples',          icon: Gift,           perm: 'samples' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: IndianRupee,
    color: { accent: 'text-teal-400', bg: 'bg-teal-500/10', ring: 'border-teal-400', sectionLabel: 'text-teal-600 dark:text-teal-400' },
    items: [
      { path: '/debtors',           label: 'Debtors',          icon: TrendingUp,     perm: 'debtors' },
      { path: '/creditors',         label: 'Creditors',        icon: TrendingDown,   perm: 'creditors' },
      { path: '/creditor-reminders',label: 'Vendor Reminders', icon: Bell,           perm: 'creditor_reminders' },
      { path: '/debtor-reminders',  label: 'Collection Cockpit', icon: Bell,         perm: 'debtor_reminders' },
      { path: '/stock-debts',       label: 'Stock Debts',      icon: AlertTriangle,  perm: 'stock_debts' },
      { path: '/cashflow',          label: 'Cashflow',         icon: IndianRupee,    perm: 'cashflow' },
      { path: '/expenses',          label: 'Operating Expenses', icon: Banknote,     perm: 'expenses', adminOnly: true },
      { path: '/profitability',     label: 'Master Profitability', icon: TrendingUp, perm: 'profitability', adminOnly: true },
      { path: '/loans',             label: 'Loans',            icon: Landmark,       perm: 'loans' },
      { path: '/overdrafts',        label: 'Overdrafts',       icon: CreditCard,     perm: 'overdrafts' },
      { path: '/credit-cards',      label: 'Credit Cards',     icon: CreditCard,     perm: 'credit_cards' },
      { path: '/bank-reconciliation', label: 'Bank Reconciliation', icon: Banknote,  perm: 'bank_reconciliation' },
      { path: '/cost-variance',     label: 'Cost Variance',    icon: FileBarChart,   perm: 'reports', adminOnly: true },
      { path: '/price-audit',       label: 'Price Audit Log',  icon: History,        perm: 'price_audit_log', adminOnly: true },
      { path: '/vouchers',          label: 'Vouchers',         icon: Receipt,        perm: 'vouchers' },
      { path: '/accounting',        label: 'Accounting',       icon: BookOpen,       perm: 'accounting' },
      { path: '/reports',           label: 'Reports',          icon: BarChart3,      perm: 'reports' },
      { path: '/historical-data',   label: 'Historical Data',  icon: History,        perm: 'historical_data' },
      { path: '/financial-reports', label: 'Financial Reports',icon: FileBarChart,   perm: 'financial_reports' },
    ],
  },
  {
    id: 'ai',
    label: 'Intelligence',
    icon: Brain,
    color: { accent: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', ring: 'border-fuchsia-400', sectionLabel: 'text-fuchsia-600 dark:text-fuchsia-400' },
    items: [
      { path: '/intelligence',    label: 'Intelligence AIs',  icon: Brain,        perm: 'feature_ai_features' },
      { path: '/insights',        label: 'Business Insights', icon: BarChart3,    perm: 'reports' },
      { path: '/procurement-analytics', label: 'Procurement Analytics', icon: TrendingUp, perm: 'reports' },
      { path: '/ai-memory',       label: 'Grievance Notices', icon: Sparkles,     perm: 'feature_grievances_widget' },
      { path: '/festive-planner', label: 'Festive Planner',   icon: PartyPopper,  perm: 'feature_ai_features' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: ShieldCheck,
    color: { accent: 'text-orange-400', bg: 'bg-orange-500/10', ring: 'border-orange-400', sectionLabel: 'text-orange-600 dark:text-orange-400' },
    items: [
      { path: '/workforce',     label: 'Workforce',     icon: Users,       perm: 'workforce' },
      { path: '/compliance',    label: 'Compliance',    icon: FileCheck,   perm: 'compliance' },
      { path: '/tasks',         label: 'Task Manager',  icon: ClipboardList, perm: 'tasks' },
      { path: '/product-codes', label: 'Item Codes',    icon: ShieldCheck, perm: 'item_codes', adminOnly: true },
      { path: '/users',         label: 'Users',         icon: ShieldCheck, perm: 'user_management' },
      { path: '/whatsapp-bot',  label: 'TI WhatsApp Bot', icon: MessageCircle, perm: 'user_management' },
      { path: '/aliases',       label: 'Fuzzy Aliases', icon: Zap,         perm: 'user_management', adminOnly: true },
      { path: '/backup',        label: 'Backup & Export', icon: Cloud,     perm: 'backup', adminOnly: true },
    ],
  },
];
